import { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import I18n from "i18n-js";
import { AppDispatch, RootState } from "app/store";
import { useDialog, useFetchUserData } from "app/hook";
import { actions as configActions } from "app/configSlice";
import { DialogType, RedirectSignUpSource } from "app/types";
import { btnDelay, usePlaySound } from "common/utils";
import { ConfirmMessageBox, WarningMessageBox } from "common/elements";
import {
  AppUser,
  UserType,
  AccountType,
  BillingType,
  ChatRoomMessageSync,
} from "features/user/types";
import {
  IconUserAdd,
  IconUserChange,
  IconUserAddInvalid,
} from "features/user/assets";
import { ParentGate } from "features/parent";
import { CreateChildUser } from "features/auth/CreateChildUser";
import { actions, selectAllChatRoomMessageSync } from "features/user/slice";
import { UserList } from "./UserList";

const MessageUnread = ({
  uid,
  chatRoomMessageSync,
}: {
  uid: string;
  chatRoomMessageSync: ChatRoomMessageSync[];
}) =>
  chatRoomMessageSync.filter(
    (sync) =>
      sync.uid === uid &&
      (dayjs(sync.latestMessageCreatedAt, "YYYY-MM-DDTHH:mm:ssZ").diff(
        dayjs(sync.latestReadAt, "YYYY-MM-DDTHH:mm:ssZ"),
      ) > 0 ||
        (sync.latestReadAt === null && sync.latestMessageCreatedAt !== null)),
  ).length ? (
    <div className="flex-col-el flex-center absolute right-0 top-0 bg-danger rounded-full w-[20px] h-[20px] z-50" />
  ) : (
    <></>
  );

const Account = ({
  user,
  handleOpen,
  chatRoomMessageSync,
}: {
  user: AppUser;
  handleOpen: () => void;
  chatRoomMessageSync: ChatRoomMessageSync[];
}) => (
  <div
    className="flex-col-center w-[92px] pointer-events-auto"
    onPointerDown={handleOpen}
  >
    <div
      className="flex-col-center cursor-pointer bg-white rounded-full w-[80px] h-[80px]"
      style={{
        boxShadow:
          "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
      }}
    >
      <MessageUnread uid={user.uid} chatRoomMessageSync={chatRoomMessageSync} />

      <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden">
        <img
          key={user.icon}
          alt="ユーザプロフィール画像"
          src={user.icon}
          className="w-full h-full object-contain"
        />
      </div>
    </div>

    <div className="flex-col-center w-[90px] h-[24px]">
      <p className="text-body text-center text-base text-gray2-100 text-body-ellipsis w-full">
        {user.display_name}
      </p>
    </div>
  </div>
);

export const UserAccount = () => {
  const play = usePlaySound();
  const navigate = useNavigate();
  const handleDialog = useDialog();
  const [list, setList] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [change, setChange] = useState(false);
  const [newUser, setNewUser] = useState(false);
  const [maxUser, setMaxUser] = useState(false);
  const { handleFetchUserData } = useFetchUserData();
  const [parentGate, setParentGate] = useState(false);
  const [changeUser, setChangeUser] = useState<AppUser | null>(null);
  const users = useSelector((state: RootState) => state.user.users);
  const parent = useSelector((state: RootState) => state.user.parent);
  const active = useSelector((state: RootState) => state.user.active);
  const chatRoomMessageSync = useSelector(selectAllChatRoomMessageSync);

  const handleShowUserList = () => {
    play();
    setList(true);
  };
  const handleCloseUserList = () => {
    play();
    setList(false);
  };

  const handleOpenAddNewUser = () => {
    play();
    if (active.account_type === AccountType.GUEST) {
      handleDialog({
        type: DialogType.REDIRECT_SIGN_UP,
        value: true,
        args: { source: RedirectSignUpSource.CHILD_USER_ACCOUNT_CREATION },
      });
    } else {
      if (users.length > 2) {
        setMaxUser(true);
      } else {
        setParentGate(true);
      }
    }
  };
  const handleCloseCreateUser = () => {
    setNewUser(false);
  };
  const handleCloseParentGate = () => {
    setParentGate(false);
  };
  const handleParentSuccess = () => {
    setParentGate(false);
    setNewUser(true);
  };

  const handleCloseWarning = () => {
    play();
    setMaxUser(false);
  };

  const handleOpenChangeUser = (user: AppUser) => () => {
    play();
    setChange(true);
    setChangeUser(user);
  };
  const handleChangeUser = () => {
    btnDelay(async () => {
      setChange(false);
      try {
        handleDialog({ type: DialogType.LOADING, value: true });
        handleFetchUserData({
          parent_uid: changeUser.parent_uid,
          uid: changeUser.uid,
        })
          .then(() => {
            dispatch(actions.setActiveUser(changeUser));
            dispatch(
              configActions.updateDialog({
                type: DialogType.USER_PROFILE,
                value: false,
              }),
            );
            handleDialog({ type: DialogType.LOADING, value: false });
            toast(
              <div>
                <p className="text text-textcolor-black">
                  {I18n.t("MSG_USER_PROFILE_CHANGE_USER_SUCCESS")}
                </p>
              </div>,
            );
            navigate("/top", { replace: true });
          })
          .catch((error) => {
            console.error(error);
            handleDialog({ type: DialogType.LOADING, value: false });
          });
      } catch (error) {
        console.error(error);
        console.error(JSON.stringify(changeUser));
        handleDialog({ type: DialogType.LOADING, value: false });
      }
    });
  };
  const handleChangeUserCancel = () => {
    play();
    setChange(false);
    setChangeUser(null);
  };

  return (
    <>
      <UserList visible={list} close={handleCloseUserList} />
      <CreateChildUser open={newUser} close={handleCloseCreateUser} />
      {maxUser && (
        <WarningMessageBox
          zIndex={10110}
          title={I18n.t("MSG_USER_PROFILE_MAX_USER_TITLE")}
          message={I18n.t("MSG_USER_PROFILE_MAX_USER_MESSAGE")}
          close={handleCloseWarning}
        />
      )}
      {change && (
        <ConfirmMessageBox
          title={I18n.t("MSG_USER_PROFILE_CHANGE_USER_TITLE")}
          message={I18n.t("MSG_USER_PROFILE_CHANGE_USER_MESSAGE", {
            name: changeUser.display_name,
          })}
          close={handleChangeUserCancel}
          confirm={handleChangeUser}
        />
      )}

      <div className="flex-col-view justify-start items-end w-[416px] h-[168px]">
        <div className="flex-col-center w-[370px] overflow-hidden">
          <div className="flex-row-center px-[8px] h-[28px] bg-orange-alt rounded-[14px]">
            <IconUserChange />
            <p className="text text-textcolor-black !text-base !px-2">
              {I18n.t("MSG_USER_PROFILE_CHANGE_USER")}
            </p>
          </div>

          <div className="flex-row-el flex-center mt-[20px] gap-1">
            {parent.billing_type === BillingType.EDUCATION ? (
              users.filter(
                (u) =>
                  u.type !== UserType.FROZEN &&
                  u.account_type !== AccountType.PARENTS,
              ).length > 4 && (
                <button
                  id="user-page-user-list-btn"
                  onClick={handleShowUserList}
                  className="opacity-button w-[92px]"
                >
                  <div className="flex-col-center">
                    <span className="material-symbols-outlined text-[#FFD1A1] text-[64px]">
                      expand_circle_down
                    </span>
                  </div>
                  <p className="text-body text-gray2-100 text-base">
                    {I18n.t("MSG_USER_PROFILE_SHOW_USER_LIST_BTN")}
                  </p>
                </button>
              )
            ) : (
              <button
                onClick={handleOpenAddNewUser}
                className="opacity-button w-[92px]"
              >
                <div className="flex-col-center w-[80px] h-[80px]">
                  {active.account_type === AccountType.KIDS &&
                  users.length < 3 ? (
                    <IconUserAdd />
                  ) : (
                    <IconUserAddInvalid />
                  )}
                </div>
                <p className="text-body text-gray2-100 text-base">
                  {I18n.t("MSG_USER_PROFILE_ADD_USER_BTN")}
                </p>
              </button>
            )}

            {users
              .filter(
                (u) =>
                  u.uid !== active.uid &&
                  u.type !== UserType.FROZEN &&
                  u.account_type !== AccountType.PARENTS,
              )
              .sort((a, b) => {
                return dayjs(a.created_at) < dayjs(b.created_at) ? 1 : -1;
              })
              .slice(0, 3)
              .map((user) => (
                <Account
                  key={user.uid}
                  user={user}
                  handleOpen={handleOpenChangeUser(user)}
                  chatRoomMessageSync={chatRoomMessageSync}
                />
              ))}
          </div>
        </div>
      </div>

      <ParentGate
        visible={parentGate}
        close={handleCloseParentGate}
        confirm={handleParentSuccess}
      />
    </>
  );
};
